import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { CompanyBilling } from 'src/models';
import {
  fetchHeadsetsBillingAction,
  fetchPackagesBillingAction,
} from 'src/services/companyBilling';

const companyBillingEntityAdapter = createEntityAdapter({
  selectId: (record: CompanyBilling) => record.id,
});

export const companyBillingSelectors =
  companyBillingEntityAdapter.getSelectors();

const initialState: {
  isHeadsetsPending: boolean | null;
  isPackagesPending: boolean | null;
  hasError: boolean;
} = {
  isHeadsetsPending: null,
  isPackagesPending: null,
  hasError: false,
};

const companyBillingSlice = createSlice({
  name: 'companyBilling',
  initialState: companyBillingEntityAdapter.getInitialState(initialState),
  reducers: {
    clearCompanyBillingState: (state) => {
      state.isHeadsetsPending = null;
      state.isPackagesPending = null;
      companyBillingEntityAdapter.setAll(state, []);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHeadsetsBillingAction.pending, (state) => {
      state.isHeadsetsPending = true;
      state.hasError = false;
    });
    builder.addCase(
      fetchHeadsetsBillingAction.fulfilled,
      (state, { meta, payload }) => {
        const company = companyBillingSelectors.selectById(
          state,
          meta.arg.companyId,
        );
        companyBillingEntityAdapter.upsertOne(state, {
          id: meta.arg.companyId,
          headsets: payload,
          packages: company?.packages || [],
        });
        state.isHeadsetsPending = false;
      },
    );
    builder.addCase(fetchHeadsetsBillingAction.rejected, (state) => {
      state.hasError = true;
      state.isHeadsetsPending = false;
    });

    builder.addCase(fetchPackagesBillingAction.pending, (state) => {
      state.isPackagesPending = true;
      state.hasError = false;
    });
    builder.addCase(
      fetchPackagesBillingAction.fulfilled,
      (state, { meta, payload }) => {
        const company = companyBillingSelectors.selectById(
          state,
          meta.arg.companyId,
        );
        companyBillingEntityAdapter.upsertOne(state, {
          id: meta.arg.companyId,
          headsets: company?.headsets || [],
          packages: payload,
        });
        state.isPackagesPending = false;
      },
    );
    builder.addCase(fetchPackagesBillingAction.rejected, (state) => {
      state.hasError = true;
      state.isPackagesPending = false;
    });
  },
});

export const { clearCompanyBillingState } = companyBillingSlice.actions;

const { reducer } = companyBillingSlice;

export default reducer;
